import { ITheme, IStyle, mergeStyleSets, getTheme, IColumn } from '@fluentui/react';
import React from 'react';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { useNavigator } from '../../../../framework/services/navigation';
import { useProjectOverviewParams, AppRoutes, toProjectSlug } from '../../../app-routes';
import { ProjectOverviewService } from '../../services/project-overview';
import { DoorType } from '../../../../entities-generated/common/door-type';
import { toPascalCase } from '../../../../framework/utils/string';
import { BwjLabelField } from '../../../../framework/components/bwj-label-field';
import { BwjCard, BwjGrid, Row, Col } from '../../../../framework/components/bwj-layout';
import { Spacing } from '../../../../framework/components/bwj-layout/styles';
import { DoorSize } from '../../../../entities-generated/common/door-size';
import { DoorHanding } from '../../../../entities-generated/common/door-handing';
import { BwjTable } from '../../../../framework/components/bwj-table';
import { BwjPalette } from '../../../../app-core/app-theme';
import { ILaborCallout } from '../../../../entities-generated/common/labor-callout';
import { CustomerSelectionListing } from '../../components/customer-selection-listing';

const getStyleClasses = (theme: ITheme) => {
  const excluded: IStyle = {
    color: BwjPalette.themeError,
    fontSize: theme.fonts.large.fontSize,
    lineHeight: theme.fonts.large.lineHeight,
    margin: '0.5em 0',
    padding: 0,
  };

  return mergeStyleSets({
    excluded,
  });
}

const toDoorSizeText = (sz: DoorSize, cutdownRequired: boolean) => {
  const doorSize = DoorSize[sz].split('_')[1].replace('x', ' x ');
  return cutdownRequired ? `${doorSize} (cutdown required)` : doorSize;
}

const columns: IColumn[] = [{
  key: 'description',
  name: 'Work Description',
  fieldName: 'description',
  minWidth: 100,
  maxWidth: 150,
  isRowHeader: true,
  isResizable: true,
  data: 'string',
  onRender: (item: ILaborCallout) => item.description,
  isPadded: true,
}, {
  key: 'customerSummary',
  name: 'Customer Explanation',
  fieldName: 'customerSummary',
  minWidth: 100,
  maxWidth: 150,
  isRowHeader: true,
  isResizable: true,
  data: 'string',
  onRender: (item: ILaborCallout) => item.customerSummary,
  isPadded: true,
}, {
  key: 'time',
  name: 'Est. Time (blocks)',
  fieldName: 'time',
  minWidth: 100,
  maxWidth: 150,
  isRowHeader: true,
  isResizable: true,
  data: 'string',
  onRender: (item: ILaborCallout) => item.time.toString(),
  isPadded: true,
}, {
  key: 'materials',
  name: 'Materials Needed',
  fieldName: 'materials',
  minWidth: 100,
  maxWidth: 150,
  isRowHeader: true,
  isResizable: true,
  data: 'string',
  onRender: (item: ILaborCallout) => {
    return item.materials.map(m => `(${m.quantity}) ${m.description}`).join(';\n');
  },
  isPadded: true,
}];

export const PageProjectOverviewInstallationSiteDoor: React.FunctionComponent = () => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const nav = useNavigator();
  const { id: projectId, site, door: doorType } = useProjectOverviewParams();
  const door = ProjectOverviewService.getDoor(site!, DoorType[toPascalCase(doorType!) as keyof typeof DoorType]);
  if(!door) {
    nav.goTo(AppRoutes.projectOverview.url(toProjectSlug(projectId)));
    return null;
  }
  return (
    <BwjPageContent contentDescription={'primary content (no subcontent)'}>
      <BwjCard>
        <BwjGrid>
          {door.isExcluded &&
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12}>
              <h3 className={cs.excluded}>{'[Door Is Excluded]'}</h3>
            </Col>
          </Row>}
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={4}>
              <BwjLabelField
                label={'Required size'}
                value={toDoorSizeText(door.size, door.cutdownRequired)}
                noBorder={true}
              />
            </Col>
            <Col small={12} large={4}>
              <BwjLabelField
                label={'Handing'}
                value={DoorHanding[door.handing]}
                noBorder={true}
              />
            </Col>
            <Col small={12} large={4}>
              <BwjLabelField
                label={'Existing door present'}
                value={door.existingDoorPresent ? 'YES' : 'NO'}
                noBorder={true}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12}>
              <h3>Customer Selections</h3>
              <CustomerSelectionListing
                door={door}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12}>
              <h3>Work Items</h3>
              <BwjTable<ILaborCallout>
                items={door.workTasks}
                columns={columns}
                getKey={labor => labor.id}
              />
            </Col>
          </Row>
        </BwjGrid>
      </BwjCard>
    </BwjPageContent>
  );
};
